import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { useExpensesByDateRange } from '../hooks/useMonthlyTransactions';

const COLOR_PALETTE = [
    '#10B981', // emerald-500
    '#3B82F6', // blue-500
    '#6366F1', // indigo-500
    '#8B5CF6', // violet-500
    '#EC4899', // pink-500
    '#F43F5E', // rose-500
    '#F59E0B', // amber-500
    '#84CC16', // lime-500
    '#06B6D4', // cyan-500
    '#F97316', // orange-500
];

const CostsByCategoryPage: React.FC = () => {
    // Calculate default dates (last 30 days)
    const getDefaultDates = () => {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        return {
            start: thirtyDaysAgo.toISOString().split('T')[0],
            end: today.toISOString().split('T')[0]
        };
    };

    const defaultDates = getDefaultDates();
    const [startDate, setStartDate] = useState<string>(defaultDates.start);
    const [endDate, setEndDate] = useState<string>(defaultDates.end);
    const [dateError, setDateError] = useState<string>('');

    // Calculate date difference and validate range
    const dateValidation = useMemo(() => {
        if (!startDate || !endDate) {
            return { isValid: false, daysDiff: 0 };
        }

        const start = new Date(startDate);
        const end = new Date(endDate);
        const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

        if (start > end) {
            setDateError('Data inicial deve ser anterior à data final');
            return { isValid: false, daysDiff };
        }

        if (daysDiff > 60) {
            setDateError('O período máximo permitido é de 60 dias');
            return { isValid: false, daysDiff };
        }

        setDateError('');
        return { isValid: true, daysDiff };
    }, [startDate, endDate]);

    // Fetch real data
    const { data: expensesData, isLoading, error } = useExpensesByDateRange({
        start_date: startDate,
        end_date: endDate,
        transaction_type: 'all'
    }, dateValidation.isValid);

    // Sort data by total amount (descending)
    const sortedData = useMemo(() => {
        if (!expensesData || expensesData.length === 0) return [];
        return [...expensesData].sort((a, b) => b.total_amount - a.total_amount);
    }, [expensesData]);

    const categoryNames = sortedData.map(item => item.category_name);
    const categoryAmounts = sortedData.map(item => item.total_amount);

    // Chart configuration
    const options: any = {
        chart: {
            type: 'bar',
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: false,
                    zoom: false,
                    zoomin: false,
                    zoomout: false,
                    pan: false,
                    reset: false
                }
            },
            redrawOnWindowResize: true,
            redrawOnParentResize: true
        },
        plotOptions: {
            bar: {
                horizontal: false,
                borderRadius: 6,
                columnWidth: '60%',
                distributed: true,
                dataLabels: {
                    position: 'top'
                }
            }
        },
        colors: COLOR_PALETTE.slice(0, categoryNames.length),
        dataLabels: {
            enabled: true,
            formatter: function (val: number) {
                return 'R$ ' + val.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            style: {
                fontSize: '12px',
                colors: ['#333'],
                fontWeight: 'bold'
            },
            offsetY: -20
        },
        legend: {
            show: false,
        },
        xaxis: {
            categories: categoryNames,
            labels: {
                style: {
                    fontSize: '12px',
                    fontWeight: 500
                },
                rotate: -45
            }
        },
        yaxis: {
            labels: {
                style: {
                    fontSize: '12px'
                },
                formatter: function (val: number) {
                    return 'R$ ' + val.toLocaleString('pt-BR', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                    });
                }
            }
        },
        tooltip: {
            custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
                const categoryData = sortedData[dataPointIndex];
                return `
                    <div class="bg-white p-3 rounded-lg shadow-lg border">
                        <div class="font-semibold text-gray-800 mb-2">${categoryData.category_name}</div>
                        <div class="text-sm space-y-1">
                            <div class="flex justify-between">
                                <span class="text-gray-600">Fluxo de caixa:</span>
                                <span class="font-medium">R$ ${categoryData.monthly_amount.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}</span>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-gray-600">Cartão:</span>
                                <span class="font-medium">R$ ${categoryData.credit_card_amount.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}</span>
                            </div>
                            <div class="flex justify-between border-t pt-1">
                                <span class="text-gray-800 font-semibold">Total:</span>
                                <span class="font-bold text-blue-600">R$ ${categoryData.total_amount.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}</span>
                            </div>
                        </div>
                    </div>
                `;
            }
        },
        title: {
            text: 'Custos por Categoria',
            align: 'center',
            style: {
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#333'
            }
        },
        grid: {
            padding: {
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header with date filters */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-6">
                        Relatório de Custos por Categoria
                    </h1>

                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="w-full sm:flex-1">
                                <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
                                    Data Inicial
                                </label>
                                <input
                                    type="date"
                                    id="start-date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div className="w-full sm:flex-1">
                                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2">
                                    Data Final
                                </label>
                                <input
                                    type="date"
                                    id="end-date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>
                        
                        <button
                            className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!dateValidation.isValid || isLoading}
                        >
                            {isLoading ? 'Carregando...' : 'Filtros Aplicados'}
                        </button>
                    </div>

                    {/* Error message */}
                    {dateError && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{dateError}</p>
                        </div>
                    )}

                    {/* Date range info */}
                    {dateValidation.isValid && (
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-sm text-blue-600">
                                Período selecionado: {dateValidation.daysDiff} dia(s) ({startDate} até {endDate})
                            </p>
                        </div>
                    )}
                </div>

                {/* Chart container */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="w-full h-96">
                        {!dateValidation.isValid ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <CurrencyDollarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-700 mb-2">Selecione um período</h3>
                                    <p className="text-sm text-gray-500">
                                        Escolha as datas inicial e final para visualizar os gastos por categoria
                                    </p>
                                </div>
                            </div>
                        ) : isLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-sm text-gray-500">Carregando dados...</p>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <div className="bg-red-50 rounded-full p-3 mx-auto mb-4 w-fit">
                                        <CurrencyDollarIcon className="h-8 w-8 text-red-500" />
                                    </div>
                                    <h3 className="text-lg font-medium text-red-700 mb-2">Erro ao carregar dados</h3>
                                    <p className="text-sm text-red-500">
                                        Tente novamente mais tarde ou verifique sua conexão
                                    </p>
                                </div>
                            </div>
                        ) : sortedData.length === 0 ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <CurrencyDollarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-700 mb-2">Nenhum dado encontrado</h3>
                                    <p className="text-sm text-gray-500">
                                        Não há gastos cadastrados no período selecionado
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <Chart
                                options={options}
                                series={[{
                                    name: 'Valor Total',
                                    data: categoryAmounts
                                }]}
                                type="bar"
                                height="100%"
                                width="100%"
                            />
                        )}
                    </div>

                    {/* Summary info - only show when there's data */}
                    {sortedData.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-sm font-medium text-gray-600">Total de Categorias</p>
                                    <p className="text-2xl font-bold text-gray-900">{categoryNames.length}</p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-sm font-medium text-gray-600">Maior Gasto</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        R$ {Math.max(...categoryAmounts).toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-sm font-medium text-gray-600">Total Geral</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        R$ {categoryAmounts.reduce((sum, amount) => sum + amount, 0).toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </p>
                                </div>
                            </div>

                            {/* Detailed breakdown table */}
                            <div className="overflow-x-auto">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalhamento por Categoria</h3>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Categoria
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Fluxo de Caixa
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Cartão
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Total
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                % do Total
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {sortedData.map((item, index) => {
                                            const totalGeneral = categoryAmounts.reduce((sum, amount) => sum + amount, 0);
                                            const percentage = ((item.total_amount / totalGeneral) * 100).toFixed(1);

                                            return (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className={`w-3 h-3 rounded-full mr-3`}
                                                                style={{ backgroundColor: COLOR_PALETTE[index] }}>
                                                            </div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {item.category_name}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        R$ {item.monthly_amount.toLocaleString('pt-BR', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        R$ {item.credit_card_amount.toLocaleString('pt-BR', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                        R$ {item.total_amount.toLocaleString('pt-BR', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {percentage}%
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CostsByCategoryPage;